/**
 * Header/footer placeholder catalog for the Generic Report Designer.
 * Uses canonical Placeholder Vocabulary tokens plus two report-shell tokens
 * (@practicelogo@, @pagenumber@) that only belong on the reusable shell.
 */

export const REPORT_PLACEHOLDER_TOKEN_RE = /@([a-z][a-z0-9]*)@/gi;

export const REPORT_PLACEHOLDER_GROUPS = [
  {
    id: "practice",
    label: "Practice",
    fields: [
      { token: "practicename", label: "Practice Name" },
      { token: "practiceaddress", label: "Practice Address" },
      { token: "practicephone", label: "Practice Phone" },
      { token: "practicefax", label: "Practice Fax" },
      { token: "practicelogo", label: "Practice Logo" },
    ],
  },
  {
    id: "patient",
    label: "Patient",
    fields: [
      { token: "fullname", label: "Patient Name" },
      { token: "dob", label: "Patient DOB" },
      { token: "mrn", label: "Patient MRN" },
    ],
  },
  {
    id: "encounter",
    label: "Encounter / Provider",
    fields: [
      { token: "dateofservice", label: "Date of Service" },
      { token: "provider", label: "Provider Name" },
      { token: "providertitle", label: "Provider Credentials" },
    ],
  },
  {
    id: "document",
    label: "Document",
    fields: [
      { token: "todaysdate", label: "Document Date" },
      { token: "pagenumber", label: "Page Number" },
      { token: "notetype", label: "Note Type" },
    ],
  },
];

export const REPORT_KNOWN_TOKENS = REPORT_PLACEHOLDER_GROUPS.flatMap((group) =>
  group.fields.map((field) => field.token),
);

export const REPORT_PLACEHOLDER_LABELS = Object.fromEntries(
  REPORT_PLACEHOLDER_GROUPS.flatMap((group) =>
    group.fields.map((field) => [`@${field.token}@`, field.label]),
  ),
);

export function reportFieldToken(token) {
  return `@${String(token || "").replace(/@/g, "").toLowerCase()}@`;
}

export function placeholderChipHtml(token, label) {
  const safeToken = reportFieldToken(token);
  const safeLabel = String(label || REPORT_PLACEHOLDER_LABELS[safeToken] || safeToken);
  return `<span class="grd-ph" data-placeholder="${safeToken}" contenteditable="false">${safeLabel}</span>`;
}

const SAMPLE_LOGO_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="48" viewBox="0 0 160 48">' +
  '<rect width="160" height="48" rx="4" fill="#0f766e"/>' +
  '<text x="80" y="30" text-anchor="middle" fill="white" font-family="Arial, Helvetica, sans-serif" font-size="14" font-weight="600">SAMPLE LOGO</text>' +
  "</svg>";

export const SAMPLE_PRACTICE_LOGO_SRC = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(SAMPLE_LOGO_SVG)}`;

export const PREVIEW_SAMPLE_VALUES = {
  practicename: "Sample Medical Practice",
  practiceaddress: "100 Main Street, Suite 200, Anytown, NY 10001",
  practicephone: "(555) 010-1000",
  practicefax: "(555) 010-1001",
  fullname: "John Smith",
  dob: "01/01/1970",
  mrn: "123456",
  dateofservice: "09/14/2026",
  provider: "Sample Physician",
  providertitle: "MD",
  todaysdate: "09/14/2026",
  notetype: "Visit Note",
};

export function previewValuesForPage(pageNumber) {
  return {
    ...PREVIEW_SAMPLE_VALUES,
    pagenumber: String(pageNumber),
    practicelogo: `<img class="grd-logo" alt="Practice logo" src="${SAMPLE_PRACTICE_LOGO_SRC}" />`,
  };
}

function valueForToken(token, values) {
  const key = String(token || "").toLowerCase();
  if (Object.prototype.hasOwnProperty.call(values, key)) {
    const value = values[key];
    return value == null ? "" : String(value);
  }
  if (REPORT_KNOWN_TOKENS.includes(key)) return "";
  return null;
}

export function resolveReportHtml(html, values = {}) {
  if (!html) return "";
  const chipRe = /<span[^>]*data-placeholder=["'](@[a-z0-9]+@)["'][^>]*>[\s\S]*?<\/span>/gi;
  const withChips = String(html).replace(chipRe, (match, token) => {
    const key = String(token).replace(/@/g, "").toLowerCase();
    const resolved = valueForToken(key, values);
    return resolved == null ? match : resolved;
  });
  return withChips.replace(REPORT_PLACEHOLDER_TOKEN_RE, (match, token) => {
    const resolved = valueForToken(token, values);
    return resolved == null ? match : resolved;
  });
}

export const SAMPLE_BODY_HTML = `
<p><strong>Visit Note</strong> — generated by the Complex Phrase for this note type. This area is not designed in the Generic Report Template.</p>
<p>Patient is a 56-year-old presenting for follow-up. History, exam, assessment, and plan are supplied at generation time.</p>
<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
`.trim();
